import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { get } from 'es-toolkit/compat'
import Overlay from 'ol/Overlay'
import type { Positioning } from 'ol/Overlay'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import Feature from 'ol/Feature'
import Point from 'ol/geom/Point'
import Polygon from 'ol/geom/Polygon'
import type MapBrowserEvent from 'ol/MapBrowserEvent'
import type { Coordinate } from 'ol/coordinate'
import { Style, Stroke, Fill, Circle as CircleStyle } from 'ol/style'
import { styled } from '@mui/system'
import { LinearProgress, Paper, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import useMap from 'hooks/use-map'
import useHarvestHoverData from 'hooks/use-harvest-hover-data'
import { pickHoverLayer } from 'common/harvest-hover/pick-layer'
import type { HoverHit } from 'common/harvest-hover/protocol'
import InfoList from 'components/map/point-info-view/info-list'
import { FloatingPanel } from 'components/floating'
import { colors } from 'components/foundation'
import { trackEvent } from 'api/usage-tracking'
import { TEST_IDS } from 'test-ids'

const HOVER_TOLERANCE_PX = 6
const POPUP_OFFSET_PX = 16
const HIGHLIGHT_Z_INDEX = 1000
const HIGHLIGHT_POINT_RADIUS_PX = 6
const HIGHLIGHT_STROKE_WIDTH_PX = 2
const HIGHLIGHT_FILL_OPACITY = 0.25
const TRACKING_SESSION_KEY = 'umami_hover_inspect_fired'

const highlightStyle = new Style({
  image: new CircleStyle({
    radius: HIGHLIGHT_POINT_RADIUS_PX,
    fill: new Fill({ color: colors.brand }),
    stroke: new Stroke({
      color: colors.white,
      width: HIGHLIGHT_STROKE_WIDTH_PX,
    }),
  }),
  fill: new Fill({ color: alpha(colors.brand, HIGHLIGHT_FILL_OPACITY) }),
  stroke: new Stroke({
    color: colors.white,
    width: HIGHLIGHT_STROKE_WIDTH_PX,
  }),
})

function toOlGeometry(hit: HoverHit) {
  return hit.geometry.type === 'Point'
    ? new Point(hit.geometry.coordinates)
    : new Polygon(hit.geometry.coordinates)
}

/**
 * Hover inspection: shows the columns of whatever point or swath box is under
 * the pointer, answered locally by a worker holding the whole harvest layer.
 * The WMS layer keeps doing the drawing; this only adds a popup and a
 * highlight for the one feature under the cursor.
 */
export default function HoverInfo() {
  const { t } = useTranslation()
  const map = useMap()
  const wmsLayers = useSelector((state) => get(state, 'map.wmsLayers', []))
  const harvestId = useSelector((state) => get(state, 'harvest.current.id')) as
    number | undefined

  const layer = useMemo(
    () => pickHoverLayer(wmsLayers, harvestId),
    [wmsLayers, harvestId],
  )
  const { status, progress, hit, setPointer, clearPointer } =
    useHarvestHoverData(harvestId, layer)

  // OpenLayers moves an overlay's element into its own container, so React
  // mustn't own that node's parent: the popup is portalled into a detached
  // element instead of rendered in place.
  const [popupElement] = useState(() => {
    const element = document.createElement('div')
    element.id = TEST_IDS.hoverInfoPopup
    element.dataset.testid = TEST_IDS.hoverInfoPopup
    return element
  })
  const overlayRef = useRef<Overlay | null>(null)
  const highlightRef = useRef<VectorSource | null>(null)
  const pointerRef = useRef<{
    coordinate: Coordinate
    positioning: Positioning
  } | null>(null)

  useEffect(() => {
    if (!sessionStorage.getItem(TRACKING_SESSION_KEY)) {
      trackEvent('hover-inspection-tool')
      sessionStorage.setItem(TRACKING_SESSION_KEY, '1')
    }
  }, [])

  useEffect(() => {
    if (!map) return
    const overlay = new Overlay({
      element: popupElement,
      stopEvent: false,
    })
    const source = new VectorSource()
    const highlight = new VectorLayer({
      source,
      style: highlightStyle,
      zIndex: HIGHLIGHT_Z_INDEX,
    })
    map.addOverlay(overlay)
    map.addLayer(highlight)
    overlayRef.current = overlay
    highlightRef.current = source
    return () => {
      map.removeOverlay(overlay)
      map.removeLayer(highlight)
      overlayRef.current = null
      highlightRef.current = null
    }
  }, [map, popupElement])

  useEffect(() => {
    if (!map) return
    let frame = 0
    let latest: MapBrowserEvent<PointerEvent> | null = null

    const query = () => {
      frame = 0
      const view = map.getView()
      const resolution = view.getResolution()
      const size = map.getSize()
      if (!latest || !resolution || !size) return
      const [px, py] = latest.pixel
      // Open the popup away from the nearest map edges so it stays on screen.
      const vertical = py > size[1] / 2 ? 'bottom' : 'top'
      const horizontal = px > size[0] / 2 ? 'right' : 'left'
      pointerRef.current = {
        coordinate: latest.coordinate,
        positioning: `${vertical}-${horizontal}` as Positioning,
      }
      const [x, y] = latest.coordinate
      setPointer(x, y, HOVER_TOLERANCE_PX * resolution)
    }

    const onMove = (evt: MapBrowserEvent<PointerEvent>) => {
      if (evt.dragging) {
        clearPointer()
        return
      }
      latest = evt
      if (!frame) frame = requestAnimationFrame(query)
    }

    const viewport = map.getViewport()
    map.on('pointermove', onMove)
    viewport.addEventListener('mouseleave', clearPointer)
    return () => {
      map.un('pointermove', onMove)
      viewport.removeEventListener('mouseleave', clearPointer)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [map, setPointer, clearPointer])

  useEffect(() => {
    const overlay = overlayRef.current
    const source = highlightRef.current
    source?.clear()
    if (!overlay) return
    if (!hit || !pointerRef.current) {
      overlay.setPosition(undefined)
      return
    }
    const { coordinate, positioning } = pointerRef.current
    const [vertical, horizontal] = positioning.split('-')
    overlay.setPositioning(positioning)
    overlay.setOffset([
      horizontal === 'left' ? POPUP_OFFSET_PX : -POPUP_OFFSET_PX,
      vertical === 'top' ? POPUP_OFFSET_PX : -POPUP_OFFSET_PX,
    ])
    overlay.setPosition(coordinate)
    source?.addFeature(new Feature({ geometry: toOlGeometry(hit) }))
  }, [hit])

  const statusMessage =
    status === 'preparing'
      ? t('hover_inspection_preparing')
      : status === 'downloading'
        ? t('hover_inspection_downloading')
        : status === 'error'
          ? t('hover_inspection_unavailable')
          : null

  return (
    <>
      {hit
        ? createPortal(
            <HoverCard elevation={3} role="tooltip">
              <Typography variant="subtitle2" component="h2">
                {layer === 'swath' ? t('swath_data') : t('point_data')}
              </Typography>
              <InfoList data={hit.properties} scrollable={false} />
            </HoverCard>,
            popupElement,
          )
        : null}
      {statusMessage ? (
        <FloatingPanel
          $position="bottom"
          id={TEST_IDS.hoverInspectionStatus}
          data-testid={TEST_IDS.hoverInspectionStatus}
        >
          <StatusBody role="status" aria-live="polite">
            <Typography variant="body2">{statusMessage}</Typography>
            {status === 'error' ? null : (
              <LinearProgress
                variant={
                  status === 'downloading' && progress !== null
                    ? 'determinate'
                    : 'indeterminate'
                }
                value={(progress ?? 0) * 100}
                aria-label={statusMessage}
              />
            )}
          </StatusBody>
        </FloatingPanel>
      ) : null}
    </>
  )
}

// The popup follows the pointer, so it must never take pointer events itself:
// it would steal the pointermove that positions it.
const HoverCard = styled(Paper)({
  pointerEvents: 'none',
  width: 'max-content',
  padding: '8px 12px',
})

const StatusBody = styled('div')({
  minWidth: '260px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
})
